<template>
  <div class="trk">
    <div class="trk__head">
      <div>
        <h4 class="mb-0">Трекер задач</h4>
        <div class="trk__subtitle">статус меняется автоматически по результату закрытия инженером</div>
      </div>
      <div class="trk__period">
        <span class="trk__period-label">с</span>
        <date-picker
          v-model="period.from"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
          :clearable="false"
          :editable="false"
          :disabled-date="disableAfterTo"
          input-class="form-control form-control-sm trk__from"
          placeholder="дд.мм.гггг"
        />
        <span class="trk__period-label">по</span>
        <date-picker
          v-model="period.to"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
          :clearable="false"
          :editable="false"
          :disabled-date="disableBeforeFrom"
          input-class="form-control form-control-sm trk__to"
          placeholder="дд.мм.гггг"
        />
        <button type="button" class="btn btn-outline-secondary btn-sm trk__refresh" :disabled="refreshing" @click="refresh">
          <span v-if="refreshing" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Обновить
        </button>
      </div>
    </div>

    <div v-if="tasks.status === 'error'" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center trk__banner trk__banner--tasks" role="alert">
      <span>Не удалось загрузить задачи: {{ errorMessage(tasks.error) }}</span>
      <button type="button" class="btn btn-sm btn-light ml-auto trk__retry" @click="reloadTasks(true)">Повторить</button>
    </div>
    <div v-if="locations.status === 'error'" class="alert alert-warning py-1 px-3 mb-2 d-flex align-items-center small trk__banner trk__banner--locations" role="alert">
      <span>Не удалось загрузить объекты: {{ errorMessage(locations.error) }} — названия моделей и кнопка «На карте» недоступны</span>
      <button type="button" class="btn btn-sm btn-light ml-auto trk__retry" @click="reloadLocations(true)">Повторить</button>
    </div>

    <div v-if="!booted" class="trk__loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
    </div>

    <template v-else-if="tasks.status !== 'error'">
      <div v-if="empty" class="trk__empty">За выбранный период задач нет</div>
      <div class="trk__board">
        <TrackerColumn
          v-for="col in columns"
          :key="col.key"
          :title="col.title"
          :tone="col.tone"
          :cards="cards[col.key]"
          :pending-ids="pendingIds"
          :disabled="actionsDisabled"
          @edit="openEditTask"
          @cancel="cancelTask"
          @reopen="reopenTask"
          @resume="resumeTask"
          @focus="focusOnMap"
        />
      </div>
    </template>

    <TaskFormModal
      v-if="taskModal"
      :key="'edit-' + taskModal.task.id"
      mode="edit"
      :location="taskModal.location"
      :task="taskModal.task"
      :engineers="engineers.data"
      :work-types="workTypes.data"
      @saved="onTaskSaved"
      @closed="onTaskClosed"
    />
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import TrackerColumn from '@/components/management/dispatcher/tracker/TrackerColumn.vue';
import TaskFormModal from '@/components/management/dispatcher/map/TaskFormModal.vue';
import * as api from '@/components/management/dispatcher/api';
import {
  createResource, loadResource, isReady, errorMessage,
} from '@/components/management/dispatcher/resource';
import { todayIso } from '@/components/management/dispatcher/format';
import { ensureReferences } from '@/components/management/dispatcher/references';
import { notifySuccess, notifyError } from '@/components/management/dispatcher/notify';
import { cancelTaskText, CANCEL_TASK_OK } from '@/components/management/dispatcher/taskPayload';
import {
  COLUMNS, bucketTasks, cardModel, readPeriod, periodQuery, isValidPeriod, samePeriod, reopenTaskText, REOPEN_TASK_OK,
  resumeTaskText, RESUME_TASK_OK,
} from '@/components/management/dispatcher/trackerLogic';

/**
 * «Трекер» section owner (KOP-38 spec §6): loads the tasks of a creation period and the enriched
 * objects, buckets the tasks into four columns and runs the card actions.
 */
export default {
  name: 'TrackerSection',
  components: { DatePicker, TrackerColumn, TaskFormModal },
  props: {
    layoutVersion: { type: Number, default: 0 },
  },
  data() {
    return {
      booted: false,
      tasks: createResource(),
      locations: createResource(),
      engineers: createResource(),
      workTypes: createResource(),
      period: readPeriod(this.$route && this.$route.query, todayIso()),
      busy: false,
      pendingIds: [],
      taskModal: null,
    };
  },
  computed: {
    columns() { return COLUMNS; },
    locationsById() {
      const out = {};
      this.locations.data.forEach((l) => { out[l.id] = l; });
      return out;
    },
    buckets() { return bucketTasks(this.tasks.data); },
    cards() {
      const today = todayIso();
      const out = {};
      COLUMNS.forEach((c) => { out[c.key] = this.buckets[c.key].map((t) => cardModel(t, this.locationsById, today)); });
      return out;
    },
    empty() { return isReady(this.tasks) && COLUMNS.every((c) => this.buckets[c.key].length === 0); },
    actionsDisabled() { return !isReady(this.tasks) || this.busy; },
    refreshing() { return this.tasks.status === 'loading' || this.locations.status === 'loading'; },
  },
  watch: {
    period: {
      deep: true,
      handler() {
        if (!isValidPeriod(this.period)) return;
        this.syncQuery();
        this.reloadTasks();
      },
    },
    '$route.query': function onQuery(query) {
      const next = readPeriod(query, todayIso());
      if (!samePeriod(next, this.period)) this.period = next;
    },
  },
  created() {
    this.inFlight = {}; // non-reactive: de-duplicates concurrent reloads per key
    this.queued = {}; // non-reactive: one coalesced trailing reload per key for `fresh` callers
    this.syncQuery();
    this.loadInitial();
  },
  methods: {
    errorMessage,
    /* ---------- period ---------- */
    syncQuery() {
      const current = (this.$route && this.$route.query) || {};
      const next = periodQuery(this.period);
      if (current.from === next.from && current.to === next.to) return;
      this.$router.replace({ query: { ...current, ...next } }).catch(() => {});
    },
    disableAfterTo(date) { return todayIso(date) > this.period.to; },
    disableBeforeFrom(date) { return todayIso(date) < this.period.from; },
    /* ---------- loading ---------- */
    async loadInitial() {
      const locations = this.reloadLocations();
      await this.reloadTasks();
      this.booted = true;
      await locations;
    },
    /**
     * Reuses an in-flight load. With `fresh`, a load already in flight is followed by one more
     * run (coalesced across callers) so a post-mutation read never settles on a pre-mutation response.
     */
    dedupe(key, run, fresh = false) {
      const current = this.inFlight[key];
      if (current) {
        if (!fresh) return current;
        if (!this.queued[key]) {
          this.queued[key] = current.then(() => { this.queued[key] = null; return this.dedupe(key, run); });
        }
        return this.queued[key];
      }
      const promise = run().finally(() => { this.inFlight[key] = null; });
      this.inFlight[key] = promise;
      return promise;
    },
    /** Loads the tasks of the period captured at call time; if the period moved meanwhile, loads once more. */
    reloadTasks(fresh = false) {
      if (!isValidPeriod(this.period)) return Promise.resolve(); // never an unbounded query
      const period = periodQuery(this.period);
      const fetch = () => api.getTasks({ created_from: period.from, created_to: period.to });
      return this.dedupe('tasks', () => loadResource(this.tasks, fetch), fresh)
        .then(() => (samePeriod(period, this.period) ? undefined : this.reloadTasks()));
    },
    reloadLocations(fresh = false) {
      return this.dedupe('locations', () => loadResource(this.locations, api.getEnrichedLocations), fresh);
    },
    refresh() {
      return Promise.all([this.reloadTasks(true), this.reloadLocations(true)]);
    },
    /* ---------- actions (spec §6.3) ---------- */
    confirm(text, okTitle) {
      return this.$bvModal.msgBoxConfirm(text, { okTitle, cancelTitle: 'Отмена', centered: true }).then((v) => v === true, () => false);
    },
    taskOf(card) {
      return this.tasks.data.find((t) => t.id === card.id) || null;
    },
    /** One confirmed status change: busy across the dialog and the request, pending id while in flight. */
    async runStatusAction(card, { text, okTitle, request, success }) {
      if (this.actionsDisabled || this.taskModal) return;
      this.busy = true; // held across the dialog so a second click is ignored
      try {
        if (!(await this.confirm(text, okTitle))) return;
        this.pendingIds = [...this.pendingIds, card.id];
        try {
          await request(card.id);
          notifySuccess(this, success);
          await this.reloadTasks(true);
        } finally {
          this.pendingIds = this.pendingIds.filter((id) => id !== card.id);
        }
      } catch (e) {
        notifyError(this, e);
        await this.reloadTasks(true); // the board may be stale (e.g. the engineer already changed the task)
      } finally {
        this.busy = false;
      }
    },
    cancelTask(card) {
      return this.runStatusAction(card, {
        text: cancelTaskText(card.id, { onMap: false }),
        okTitle: CANCEL_TASK_OK,
        request: api.blockTask,
        success: `Задача T‑${card.id} отменена`,
      });
    },
    reopenTask(card) {
      return this.runStatusAction(card, {
        text: reopenTaskText(card.id),
        okTitle: REOPEN_TASK_OK,
        request: api.reopenTask,
        success: `Задача T‑${card.id} открыта повторно`,
      });
    },
    resumeTask(card) {
      return this.runStatusAction(card, {
        text: resumeTaskText(card.id),
        okTitle: RESUME_TASK_OK,
        request: api.reopenTask,
        success: `Задача T‑${card.id} возвращена в работу`,
      });
    },
    async openEditTask(card) {
      if (this.actionsDisabled || this.taskModal) return;
      const task = this.taskOf(card);
      const location = (task && (this.locationsById[task.geo_location_id] || task.geo_location)) || null;
      if (!task || !location) {
        notifyError(this, 'Объект задачи не найден');
        return;
      }
      this.busy = true;
      try {
        if (await ensureReferences(this)) this.taskModal = { location, task };
      } finally {
        this.busy = false;
      }
    },
    onTaskSaved() {
      this.taskModal = null;
      return this.reloadTasks(true);
    },
    onTaskClosed() {
      this.taskModal = null;
    },
    focusOnMap(card) {
      if (!card.canFocus) return;
      const { href } = this.$router.resolve({ name: 'dispatcher-map', query: { focus: String(card.geoLocationId) } });
      window.open(href, '_blank', 'noopener');
    },
  },
};
</script>

<style scoped lang="scss">
.trk { display: flex; flex-direction: column; height: 100%; min-height: 0; background: #fff; border: 1px solid #e3e5e1; border-radius: 8px; padding: 12px 16px; }
.trk__head { display: flex; align-items: flex-start; gap: 14px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #eef0ec; margin-bottom: 10px; }
.trk__subtitle { color: #8b918c; font-size: 12.5px; }
.trk__period { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.trk__period-label { color: #4c524d; font-size: 13px; }
.trk__period ::v-deep .mx-datepicker { width: 140px; }
.trk__loading { flex: 1; display: flex; align-items: center; justify-content: center; }
.trk__empty { color: #8b918c; font-size: 13px; margin-bottom: 8px; }
.trk__board { flex: 1; min-height: 0; display: flex; gap: 12px; overflow-x: auto; padding-bottom: 4px; }
</style>
